import { makeInactiveOnCondition } from "@/components/pitchCorrection/inactiveOnCondition";
import { RotaryDial } from "@/components/pitchCorrection/RotaryDial";
import { handleKnobVibratoChangeInternal } from "@/components/pitchCorrection/vibratoKnob/handleKnobVibratoChangeInternal";
import { pitchDiffsFromSemitoneData } from "@/components/pitchCorrection/vibratoKnob/pitchDiffsFromSemitoneData";
import { useNamedKey } from "@/lib/zustand/useKey";
import { useNamedPitchCorrection } from "@/lib/zustand/usePitchCorrection";
import { Undoable } from "@/types/defs/pitchCorrection/undoable";

export function VibratoKnob({
  inactiveCondition,
  disabled,
  onEnd,
  onChange,
  onStart,
  namespace,
  keyNamespace,
  size = 40,
  sensitivity = 0.3,
}: {
  inactiveCondition: boolean;
  namespace: string;
  // The key's own namespace, matching the hosting editor's keyNamespace — see PitchKnob: the track-namespace read returned the untouched 'No Key' default, and the undo snapshot captured here then clobbered the shared project key on restore.
  keyNamespace: string;
  disabled?: boolean;
  onStart?: (v: number) => void;
  onChange?: (v: number) => void;
  onEnd?: (v: number) => void;
  size?: number;
  sensitivity?: number;
}) {
  const usePitchCorrection = useNamedPitchCorrection((state) =>
    state.getNamedStore(namespace)
  );
  const useKey = useNamedKey((state) => state.getNamedStore(keyNamespace));
  const key = useKey((state) => state.key);
  const knobVibratoValue = usePitchCorrection((state) => state.vibratoLevel);
  const setKnobVibratoValue = usePitchCorrection(
    (state) => state.setVibratoLevel
  );
  const setPitches = usePitchCorrection((state) => state.setPitches);
  const previousVibratoLevel = usePitchCorrection(
    (state) => state.previousVibratoLevel
  );
  const previousPitchLevel = usePitchCorrection(
    (state) => state.previousPitchLevel
  );
  const previousVibratoPitches = usePitchCorrection(
    (state) => state.previousVibratoPitches
  );
  const pitches = usePitchCorrection((state) => state.pitches);
  const originalPitches = usePitchCorrection((state) => state.originalPitches);
  const circles = usePitchCorrection((state) => state.circles);
  const setPreviousVibratoLevel = usePitchCorrection(
    (state) => state.setPreviousVibratoLevel
  );
  const setPreviousVibratoPitches = usePitchCorrection(
    (state) => state.setPreviousVibratoPitches
  );
  const unsetPreviousVibratoPitches = usePitchCorrection(
    (state) => state.unsetPreviousVibratoPitches
  );
  const addToUndoStack_ = usePitchCorrection((state) => state.addToUndoStack);
  const setRedoStack = usePitchCorrection((state) => state.setRedoStack);
  const addToUndoStack = (a: Undoable) => {
    addToUndoStack_(a);
    setRedoStack([]);
  };
  const updatePitchesAndUndoStack = (knobValue: number) => {
    const pitchDiffs =
      originalPitches.length > 0 && circles.length > 0 ?
        pitchDiffsFromSemitoneData(originalPitches, circles)
      : [];
    const newPitches = handleKnobVibratoChangeInternal(
      knobValue,
      previousVibratoLevel,
      previousVibratoPitches ?? pitches,
      pitchDiffs
    );
    addToUndoStack({
      time: new Date(),
      source: "edit",
      value: {
        pitches: pitches.slice(),
        vibratoLevel: previousVibratoLevel,
        pitchLevel: previousPitchLevel,
        key,
      },
    });
    setPitches(newPitches);
  };
  return (
    <RotaryDial
      value={knobVibratoValue}
      disabled={disabled}
      setValue={setKnobVibratoValue}
      size={size}
      onStart={makeInactiveOnCondition(
        inactiveCondition,
        onStart ?? (() => {})
      )}
      sensitivity={sensitivity}
      onEnd={makeInactiveOnCondition(inactiveCondition, (v) => {
        setPreviousVibratoLevel(v);
        unsetPreviousVibratoPitches();
        if (onEnd !== undefined) {
          onEnd(v);
        }
      })}
      onChange={makeInactiveOnCondition(inactiveCondition, (n) => {
        if (previousVibratoPitches === null) {
          setPreviousVibratoPitches(pitches);
        }
        setKnobVibratoValue(n);
        // we only update the pitches if there are pitches to update otherwise, the code above will cache the values in zustand and they'll be used on first render
        if (pitches.length > 0) {
          updatePitchesAndUndoStack(n);
        }
        if (onChange !== undefined) {
          onChange(n);
        }
      })}
    />
  );
}
